import { Loader2 } from 'lucide-react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
    Line,
    LineChart,
} from 'recharts';
import { useGa4WebAudit } from '@/hooks/useGa4WebAudit';
import { esDate } from '@/components/seo/format';

const tooltipStyle = {
    background: 'hsl(222 47% 9%)',
    border: '1px solid hsl(222 30% 16%)',
    borderRadius: '8px',
    fontSize: '12px',
};

export function Ga4OverviewPanel({ days }: { days: number }) {
    const { data, isLoading, error } = useGa4WebAudit(days);

    if (isLoading) return <Loader2 className="animate-spin text-muted-foreground" size={18} />;
    if (error) {
        return (
            <p className="text-sm text-rose-500">
                {error instanceof Error ? error.message : 'No se pudo cargar GA4.'}
            </p>
        );
    }
    if (!data) return null;

    const hasSeries = data.series.some((p) => p.active_users > 0 || p.page_views > 0);

    return (
        <div className="space-y-6">
            <p className="text-sm text-muted-foreground">
                Stream landing (www.aipeces.com).
                {data.data_through ? ` Datos hasta ${esDate(data.data_through)}.` : ' Aún no hay sync.'}
                {data.finished_at ? ` Última sync ${esDate(data.finished_at.slice(0, 10))}.` : ''}
                {data.pages_truncated ? ' Extracción de URLs truncada.' : ''}
            </p>

            {!hasSeries && (
                <p className="text-sm text-muted-foreground">
                    Sin filas en caché. El cron <code className="text-xs">ga4-sync-cron</code> (02:15 UTC) las irá
                    rellenando; en local puedes ejecutar el sync manualmente contra Key Vault.
                </p>
            )}

            {hasSeries && (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <section className="rounded-xl border border-border bg-card p-4">
                        <h3 className="text-sm font-semibold">Usuarios (page_view)</h3>
                        <div className="mt-3 h-56">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={data.series}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                    <XAxis
                                        dataKey="day"
                                        tickFormatter={(v) => v.slice(5)}
                                        tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                                    />
                                    <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} />
                                    <Tooltip contentStyle={tooltipStyle} />
                                    <Line
                                        type="monotone"
                                        dataKey="active_users"
                                        name="Usuarios"
                                        stroke="hsl(var(--primary))"
                                        strokeWidth={2}
                                        dot={false}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </section>
                    <section className="rounded-xl border border-border bg-card p-4">
                        <h3 className="text-sm font-semibold">Eventos top</h3>
                        <div className="mt-3 h-56">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={data.top_events.slice(0, 12)} layout="vertical" margin={{ left: 8 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                    <XAxis type="number" tick={{ fontSize: 10 }} />
                                    <YAxis
                                        type="category"
                                        dataKey="event_name"
                                        width={100}
                                        tick={{ fontSize: 10 }}
                                    />
                                    <Tooltip contentStyle={tooltipStyle} />
                                    <Bar dataKey="active_users" name="Usuarios" fill="hsl(var(--primary))" radius={4} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </section>
                </div>
            )}
        </div>
    );
}
